import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Filter,
  Eye,
  Calendar,
  User,
  Mail,
  Phone,
  CreditCard,
  Home,
  Download,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import useAuth from "../../../contexts/AuthContext";
import AlliedDashboardStats from "../components/DashboardStats";
import Loader from "../../../common_components/Loader";
import { ApplicationStatus, PaymentStatus, matchStatus } from "../../../types/status";

interface Payment {
  status: PaymentStatus
}

interface Application {
  applicationId: string;
  candidateName: string;
  email: string;
  mobile: string;
  category: string;
  neetRoll: string;
  neetAIRank?: number;
  payment: Payment;
  status: string;
  createdAt: string;
}

interface Filters {
  q: string;
  category: string;
  paymentStatus: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  page: number;
  limit: number;
  sort: string;
  dobFrom: string;
  dobTo: string;
  neetAIRankMin: string;
  neetAIRankMax: string;
  percentageMin: string;
  percentageMax: string;
  state: string;
  gender: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  total: number;
}

const AlliedApplicationDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { admissionId } = useParams<{ admissionId: string }>();
  const { apiRequest, handleDownload } = useAuth();
  const [applications, setApplications] = useState<Application[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const url = admissionId
        ? `/applications/dashboard-stats?admissionId=${admissionId}`
        : "/applications/dashboard-stats";
      const data = await apiRequest(url);
      setStats(data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    }
  }, [admissionId, apiRequest]);

  useEffect(() => {
    fetchDashboardStats();
  }, [admissionId, fetchDashboardStats]);

  const [filters, setFilters] = useState<Filters>({
    q: "",
    category: "",
    paymentStatus: "",
    status: "",
    dateFrom: "",
    dateTo: "",
    page: 1,
    limit: 10,
    sort: "-createdAt",
    dobFrom: "",
    dobTo: "",
    neetAIRankMin: "",
    neetAIRankMax: "",
    percentageMin: "",
    percentageMax: "",
    state: "",
    gender: "",
  });

  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {};
      (Object.keys(filters) as Array<keyof Filters>).forEach((key) => {
        if (filters[key] !== "" && filters[key] !== null) {
          params[key] = filters[key] as string | number;
        }
      });

      const queryString = new URLSearchParams(
        Object.entries(params).reduce((acc, [k, v]) => ({ ...acc, [k]: String(v) }), {})
      ).toString();
      const url = queryString
        ? `/applications/${admissionId}/all?${queryString}`
        : `/applications/${admissionId}/all`;

      const response = await apiRequest(url);
      setApplications(response.applications);
      setPagination({
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        total: response.total,
      });
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  }, [filters, apiRequest, admissionId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchApplications();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [fetchApplications]);

  const handleFilterChange = (key: keyof Filters, value: string | number) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== "page" ? 1 : (value as number),
    }));
  };

  const getStatusColor = (status: string): string => {
    const s = status ? status.toUpperCase() : "";
    const colors: Record<string, string> = {
      [ApplicationStatus.DRAFT]: "bg-yellow-100 text-yellow-800",
      [ApplicationStatus.SUBMITTED]: "bg-blue-100 text-blue-800",
      [ApplicationStatus.ACCEPTED]: "bg-green-100 text-green-800",
      [ApplicationStatus.REJECTED]: "bg-red-100 text-red-800",
    };
    return colors[s] || "bg-gray-100 text-gray-800";
  };

  const getPaymentStatusColor = (status: string): string => {
    if (matchStatus(status, PaymentStatus.VERIFIED)) {
      return "bg-emerald-100 text-emerald-800 border border-emerald-200";
    }
    if (matchStatus(status, PaymentStatus.PAID)) {
      return "bg-green-100 text-green-800 border border-green-200";
    }
    if (matchStatus(status, PaymentStatus.PENDING)) {
      return "bg-yellow-100 text-yellow-800 border border-yellow-200";
    }
    return "bg-gray-100 text-gray-800 border border-gray-200";
  };

  const handleRowClick = (applicationId: string) => {
    navigate(`/allied/application/${applicationId}/view`);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 font-sans">
      {loading && <Loader text="Loading applications..." />}

      {/* Dashboard Stats Section */}
      <div className="max-w-7xl mx-auto mb-8">
        <header className="mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold text-slate-900 mb-1">
              Allied Applications Dashboard
            </h1>
            <p className="text-slate-500">
              Comprehensive overview of Allied Health Sciences applications
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/allied")}
              className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-slate-900 transition border border-slate-700 text-sm font-medium"
            >
              <Home size={18} />
              Home
            </button>
            <button
              onClick={() =>
                handleDownload(
                  `/applications/export/xlsx-stream?admissionId=${admissionId}`,
                  "allied_applications.xlsx"
                )
              }
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg shadow-sm hover:bg-green-700 transition text-sm font-medium"
            >
              <Download size={18} />
              Export
            </button>
          </div>
        </header>
        <AlliedDashboardStats stats={stats} />
      </div>

      {/* Applications List Section */}
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-serif font-bold text-slate-900 mb-2">
            Applications Management
          </h2>
          <p className="text-slate-600">
            Search, filter, and manage Allied Health Sciences applications
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, NEET roll, or mobile..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.q}
                onChange={(e) => handleFilterChange("q", e.target.value)}
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span>{showFilters ? "Hide" : "Show"} Filters</span>
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6 pt-6 border-t border-gray-200">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Category</label>
                <select
                  className="filter-input"
                  value={filters.category}
                  onChange={(e) => handleFilterChange("category", e.target.value)}
                >
                  <option value="">All Categories</option>
                  <option value="UR">UR</option>
                  <option value="SC">SC</option>
                  <option value="ST">ST</option>
                  <option value="OBC">OBC</option>
                  <option value="EWS">EWS</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Payment Status</label>
                <select
                  className="filter-input"
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange("paymentStatus", e.target.value)}
                >
                  <option value="">All Payments</option>
                  <option value={PaymentStatus.PENDING}>Pending</option>
                  <option value={PaymentStatus.PAID}>Paid</option>
                  <option value={PaymentStatus.VERIFIED}>Verified</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Application Status</label>
                <select
                  className="filter-input"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value={ApplicationStatus.DRAFT}>Draft</option>
                  <option value={ApplicationStatus.SUBMITTED}>Submitted</option>
                  <option value={ApplicationStatus.ACCEPTED}>Accepted</option>
                  <option value={ApplicationStatus.REJECTED}>Rejected</option>
                </select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">NEET AI Rank Range</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min Rank"
                    className="filter-input"
                    value={filters.neetAIRankMin}
                    onChange={(e) => handleFilterChange("neetAIRankMin", e.target.value)}
                  />
                  <input
                    type="number"
                    placeholder="Max Rank"
                    className="filter-input"
                    value={filters.neetAIRankMax}
                    onChange={(e) => handleFilterChange("neetAIRankMax", e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">12th Percentage Range (%)</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min %"
                    className="filter-input"
                    value={filters.percentageMin}
                    onChange={(e) => handleFilterChange("percentageMin", e.target.value)}
                  />
                  <input
                    type="number"
                    placeholder="Max %"
                    className="filter-input"
                    value={filters.percentageMax}
                    onChange={(e) => handleFilterChange("percentageMax", e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">Date of Birth Range</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    className="filter-input"
                    value={filters.dobFrom}
                    onChange={(e) => handleFilterChange("dobFrom", e.target.value)}
                  />
                  <input
                    type="date"
                    className="filter-input"
                    value={filters.dobTo}
                    onChange={(e) => handleFilterChange("dobTo", e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">State</label>
                <input
                  type="text"
                  placeholder="e.g., Telangana"
                  className="filter-input"
                  value={filters.state}
                  onChange={(e) => handleFilterChange("state", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Gender</label>
                <select
                  className="filter-input"
                  value={filters.gender}
                  onChange={(e) => handleFilterChange("gender", e.target.value)}
                >
                  <option value="">All Genders</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="T">Transgender</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="text-sm text-gray-600">
            Showing {(pagination.currentPage - 1) * filters.limit + 1} to{" "}
            {Math.min(pagination.currentPage * filters.limit, pagination.total)}{" "}
            of {pagination.total} applications
          </div>
          <select
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            value={filters.sort}
            onChange={(e) => handleFilterChange("sort", e.target.value)}
          >
            <option value="-createdAt">Newest First</option>
            <option value="createdAt">Oldest First</option>
            <option value="candidateName">Name A-Z</option>
            <option value="-candidateName">Name Z-A</option>
            <option value="neetAIRank">Rank (Low to High)</option>
            <option value="-neetAIRank">Rank (High to Low)</option>
            <option value="dob">Age (Oldest First)</option>
            <option value="-dob">Age (Youngest First)</option>
            <option value="-intermediateSchool.percentage">Percentage (High-Low)</option>
            <option value="intermediateSchool.percentage">Percentage (Low-High)</option>
          </select>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">Candidate</th>
                  <th className="table-header">Course & Category</th>
                  <th className="table-header">NEET Details</th>
                  <th className="table-header">Payment</th>
                  <th className="table-header">Applied Date</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {applications.length > 0 ? (
                  applications.map((app) => (
                    <tr
                      key={app.applicationId}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(app.applicationId)}
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-9 w-9">
                            <div className="h-9 w-9 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                              <User className="h-4 w-4 text-white" />
                            </div>
                          </div>
                          <div className="ml-3 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-[180px]" title={app.candidateName}>
                              {app.candidateName}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1 truncate max-w-[180px]" title={app.email}>
                              <Mail className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{app.email}</span>
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Phone className="h-3 w-3 flex-shrink-0" />
                              {app.mobile}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 max-w-[260px]">
                        <div className="font-semibold text-slate-900 mb-1">{app.category || "UR"} Category</div>
                        {(app as any).preferenceOrder && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(Array.isArray((app as any).preferenceOrder) ? (app as any).preferenceOrder : [(app as any).preferenceOrder]).map((pref: string, idx: number) => (
                              <span key={idx} className="inline-block text-[11px] bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded break-words max-w-full leading-tight">
                                {idx + 1}. {pref}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-sm">
                        <div className="font-medium text-gray-900">Roll: {app.neetRoll}</div>
                        <div className="text-xs text-gray-500">
                          AIR: {app.neetAIRank?.toLocaleString() || "N/A"}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getPaymentStatusColor(
                            app.payment.status
                          )}`}
                        >
                          <CreditCard className="h-3 w-3 mr-1" />
                          {matchStatus(app.payment.status, PaymentStatus.VERIFIED)
                            ? "VERIFIED"
                            : matchStatus(app.payment.status, PaymentStatus.PAID)
                              ? "PAID"
                              : "PENDING"}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(app.createdAt).toLocaleDateString("en-IN")}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            app.status
                          )}`}
                        >
                          {app.status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <div
                          className="flex items-center gap-2 text-xs"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => navigate(`/allied/application/${app.applicationId}/view`)}
                            className="text-blue-600 hover:text-blue-900 font-semibold flex items-center gap-1"
                          >
                            <Eye className="h-3.5 w-3.5" /> View
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => navigate(`/allied/admin/application/${app.applicationId}/review`)}
                            className="text-emerald-600 hover:text-emerald-900 font-semibold"
                          >
                            Review
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-gray-500">
                      No applications found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {pagination.total > 0 && (
        <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show:</span>
              <select
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.limit}
                onChange={(e) => handleFilterChange("limit", parseInt(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">per page</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleFilterChange("page", 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              <button
                onClick={() => handleFilterChange("page", pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {(() => {
                  const { currentPage, totalPages } = pagination;
                  const pages: React.ReactNode[] = [];
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, currentPage + 2);

                  if (endPage - startPage < 4) {
                    if (startPage === 1) {
                      endPage = Math.min(totalPages, startPage + 4);
                    } else {
                      startPage = Math.max(1, endPage - 4);
                    }
                  }

                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => handleFilterChange("page", 1)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="start-ellipsis" className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handleFilterChange("page", i)}
                        className={`px-3 py-1 text-sm border rounded-lg transition-colors ${i === currentPage
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-300 hover:bg-gray-50"
                          }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="end-ellipsis" className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => handleFilterChange("page", totalPages)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              <button
                onClick={() => handleFilterChange("page", pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
              <button
                onClick={() => handleFilterChange("page", pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>

            <div className="text-sm text-gray-600">
              Page {pagination.currentPage} of {pagination.totalPages}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlliedApplicationDashboard;
